import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'

import { cn } from '@/lib/utils'

/**
 * Presentational Toast primitives (dep-free — not Radix). The state/queue/
 * auto-dismiss machinery lives in <ToastProvider> / useToast()
 * (src/components/overlay/ToastProvider.tsx), which renders these.
 *
 * Variants map to the two visual languages already in the app:
 *  - `error`   → red banner with AlertTriangle (the shared ErrorToast look).
 *  - `warning` → amber banner (the duplicated procurement inline toast look).
 * Accessibility: error/warning announce assertively (`role="alert"`); the rest
 * are polite (`role="status"`).
 *
 * The entrance animation is intentionally NOT baked in (the `-translate-x-1/2`
 * `animate-fade-in` keyframe assumes a single top-centered toast and would
 * misplace stacked toasts). The provider applies a stacking-safe entrance.
 */

const toastVariants = cva(
  'pointer-events-auto relative flex items-start gap-2.5 rounded-xl px-4 py-2.5 text-sm shadow-xl',
  {
    variants: {
      variant: {
        default: 'border border-border bg-background text-foreground',
        error: 'bg-destructive text-white',
        warning: 'border border-amber-200 bg-amber-50 text-amber-900',
        success: 'bg-green-600 text-white',
        info: 'bg-slate-800 text-white',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

type ToastVariant = NonNullable<VariantProps<typeof toastVariants>['variant']>

const VARIANT_ICON: Record<ToastVariant, React.ReactNode> = {
  default: <Info className="mt-0.5 h-4 w-4 shrink-0 opacity-80" aria-hidden="true" />,
  error: <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />,
  warning: <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />,
  success: <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />,
  info: <Info className="mt-0.5 h-4 w-4 shrink-0 opacity-90" aria-hidden="true" />,
}

export interface ToastProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role'>,
    VariantProps<typeof toastVariants> {
  /** Override the variant's default icon; pass `false` to render none. */
  icon?: React.ReactNode | false
  /** When provided, renders a close button as the last child (see ErrorToast). */
  onClose?: () => void
  /** Accessible label for the close button. */
  closeLabel?: string
  /** Optional action button (e.g. "Reload" on update-consent toasts). */
  action?: { label: React.ReactNode; onClick: () => void }
}

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  (
    { className, variant = 'default', icon, onClose, closeLabel = 'Close', action, children, ...props },
    ref,
  ) => {
    const assertive = variant === 'error' || variant === 'warning'
    const onDarkBg = variant === 'error' || variant === 'success' || variant === 'info'
    return (
      <div
        ref={ref}
        role={assertive ? 'alert' : 'status'}
        aria-live={assertive ? 'assertive' : 'polite'}
        className={cn(toastVariants({ variant }), className)}
        {...props}
      >
        {icon === false ? null : icon ?? VARIANT_ICON[variant ?? 'default']}
        <div className="flex-1 min-w-0">{children}</div>
        {action ? (
          <button
            type="button"
            onClick={() => action.onClick()}
            className={cn(
              'self-center shrink-0 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              onDarkBg
                ? 'border-white/30 text-current hover:bg-white/10'
                : 'border-border text-foreground hover:bg-accent',
            )}
          >
            {action.label}
          </button>
        ) : null}
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="shrink-0 rounded-sm p-0.5 opacity-80 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    )
  },
)
Toast.displayName = 'Toast'

export const ToastTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-sm font-semibold leading-snug', className)} {...props} />
  ),
)
ToastTitle.displayName = 'ToastTitle'

export const ToastDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm leading-snug opacity-90', className)} {...props} />
))
ToastDescription.displayName = 'ToastDescription'

export type { ToastVariant }
